import { Injectable, inject } from '@angular/core';
import { SavedReportService } from '../reports/saved-report.service';
import { AssetInstanceService } from '../services/asset-instance.service';
import { CheckPointService } from '../services/checkPoint.service';
import { DatabaseService } from '../services/database.service';
import { FormDefinitionService } from '../services/form-definition.service';
import { FormService } from '../services/form.service';
import { LoggerService } from '../services/logger.service';
import {
  SAMPLE_SCENARIOS,
  SAMPLE_SCENARIO_IDS,
  SampleScenarioId,
} from './sample-scenarios';

/** A sample scenario currently present in the org's data. */
export interface InstalledSampleScenario {
  id: SampleScenarioId;
  /** In the scenario definition's order, so the tour's "first checkpoint" is stable. */
  checkpointIds: string[];
  formId: string | null;
  /** In the scenario definition's order. */
  reportIds: string[];
  /** Submissions recorded at the sample checkpoints or with the sample form. */
  submissionCount: number;
}

export type ApplySampleScenarioResult =
  | { success: true; installed: InstalledSampleScenario }
  | { success: false; reason: 'already_installed' };

export type RemoveSampleScenarioResult =
  | { success: true; removed: Record<string, number> }
  | { success: false; reason: 'not_installed' };

/** Tables touched by seeding/removal, for refreshing reactive queries. */
const SAMPLE_TABLES = [
  'form_definitions',
  'interaction_types',
  'assets',
  'asset_interaction_types',
  'asset_instances',
  'barcode_mappings',
  'interaction_records',
  'form_blobs',
  'report_definitions',
];

const BLOB_REF = /blob:sha256:([0-9a-f]{64})/g;

/**
 * Seeds and removes onboarding sample scenarios (see sample-scenarios.ts).
 *
 * Everything is created through the regular local-first services, so it syncs
 * like user data. Seeded rows are tagged in their `data` JSON
 * (`sample_scenario` on checkpoints and forms, `sampleScenario` on report
 * definitions). Removal finds them by tag rather than from a local list, so
 * it works on any device and for any admin, and it also removes everything
 * that hangs off them: submissions (and photos only they reference), labels,
 * NFC tag mappings, numbered copies and form assignments.
 *
 * Tagged rows don't count toward plan limits (FormService.count,
 * CheckPointService.count and the billing usage endpoint).
 */
@Injectable({ providedIn: 'root' })
export class SampleScenarioService {
  private db = inject(DatabaseService);
  private formService = inject(FormService);
  private formDefinitionService = inject(FormDefinitionService);
  private checkPointService = inject(CheckPointService);
  private assetInstanceService = inject(AssetInstanceService);
  private savedReportService = inject(SavedReportService);
  private log = inject(LoggerService).getLogger('sample-scenarios');

  /** All sample scenarios present in the org's data, keyed by scenario id. */
  async getInstalled(): Promise<Map<SampleScenarioId, InstalledSampleScenario>> {
    await this.db.init();

    const [checkpointRows, formRows, reportRows] = await Promise.all([
      this.db.exec(
        `SELECT id, json_extract(data, '$.sample_scenario') AS scenario, json_extract(data, '$.name') AS name
         FROM assets WHERE json_extract(data, '$.sample_scenario') IS NOT NULL`,
      ),
      this.db.exec(
        `SELECT id, json_extract(data, '$.sample_scenario') AS scenario
         FROM interaction_types WHERE json_extract(data, '$.sample_scenario') IS NOT NULL`,
      ),
      this.db.exec(
        `SELECT id, name, json_extract(data, '$.sampleScenario') AS scenario
         FROM report_definitions WHERE json_extract(data, '$.sampleScenario') IS NOT NULL`,
      ),
    ]);

    const installed = new Map<SampleScenarioId, InstalledSampleScenario>();
    const entry = (id: SampleScenarioId): InstalledSampleScenario => {
      let e = installed.get(id);
      if (!e) {
        e = { id, checkpointIds: [], formId: null, reportIds: [], submissionCount: 0 };
        installed.set(id, e);
      }
      return e;
    };

    for (const id of SAMPLE_SCENARIO_IDS) {
      const def = SAMPLE_SCENARIOS[id];
      const cps = checkpointRows.filter((r) => r.scenario === id);
      const reports = reportRows.filter((r) => r.scenario === id);
      const form = formRows.find((r) => r.scenario === id);
      if (cps.length === 0 && reports.length === 0 && !form) continue;

      const e = entry(id);
      e.formId = form ? String(form.id) : null;
      e.checkpointIds = sortByDefinitionOrder(
        cps,
        def.checkpoints.map((c) => c.name),
      ).map((r) => String(r.id));
      e.reportIds = sortByDefinitionOrder(
        reports,
        def.reports({ formId: '' }).map((r) => r.name),
      ).map((r) => String(r.id));
    }

    for (const e of installed.values()) {
      e.submissionCount = await this.countSubmissions(e.checkpointIds, e.formId ? [e.formId] : []);
    }
    return installed;
  }

  async getInstalledScenario(id: SampleScenarioId): Promise<InstalledSampleScenario | null> {
    return (await this.getInstalled()).get(id) ?? null;
  }

  /** Seeds a scenario's form, checkpoints (with numbered copies) and saved reports. */
  async apply(id: SampleScenarioId): Promise<ApplySampleScenarioResult> {
    if (await this.getInstalledScenario(id)) {
      return { success: false, reason: 'already_installed' };
    }

    const def = SAMPLE_SCENARIOS[id];
    this.log.info(`Seeding sample scenario '${id}'`);

    try {
      const formDefinitionId = this.db.generateId();
      await this.formDefinitionService.saveFormDefinition(
        formDefinitionId,
        def.form.fields,
        def.form.name,
        def.form.columns,
      );
      const form = await this.formService.create({
        name: def.form.name,
        form_definition_id: formDefinitionId,
        auto_submit: def.form.auto_submit,
        play_success_sound: def.form.play_success_sound,
        sample_scenario: id,
      });

      for (const spec of def.checkpoints) {
        const checkPoint = await this.checkPointService.create({
          name: spec.name,
          description: spec.description,
          sample_scenario: id,
        });
        await this.formService.assignToCheckPoint(checkPoint.id!, form.id!);
        for (const serial of spec.instances ?? []) {
          await this.assetInstanceService.create({
            asset_id: checkPoint.id!,
            serial_number: serial,
          });
        }
      }

      for (const report of def.reports({ formId: form.id! })) {
        await this.savedReportService.create({
          ...report,
          visibility: 'private',
          sampleScenario: id,
        });
      }
    } catch (err) {
      // Leave no half-seeded scenario behind: it would show as "installed" and
      // couldn't be re-applied. Then surface the original failure.
      this.log.error(`Seeding sample scenario '${id}' failed, rolling back:`, err);
      await this.remove(id).catch((cleanupErr) =>
        this.log.error(`Rollback of sample scenario '${id}' failed:`, cleanupErr),
      );
      throw err;
    } finally {
      this.notifyAll();
    }

    const installed = await this.getInstalledScenario(id);
    if (!installed) {
      throw new Error(`Sample scenario '${id}' not found after seeding`);
    }
    return { success: true, installed };
  }

  /**
   * Deletes a scenario's rows and everything attached to them, including
   * submissions recorded at the sample checkpoints or with the sample form.
   * Deletes run one row at a time (`WHERE id = ?`) because that's the shape
   * DatabaseService's sync tracking records as a DELETE.
   */
  async remove(id: SampleScenarioId): Promise<RemoveSampleScenarioResult> {
    await this.db.init();

    const checkpointIds = await this.ids(
      `SELECT id FROM assets WHERE json_extract(data, '$.sample_scenario') = ?`,
      [id],
    );
    const formRows = await this.db.exec(
      `SELECT id, json_extract(data, '$.form_definition_id') AS definition_id
       FROM interaction_types WHERE json_extract(data, '$.sample_scenario') = ?`,
      [id],
    );
    const formIds = formRows.map((r) => String(r.id));
    // Reports tagged as samples, plus any built on the sample form (e.g. a copy
    // saved from a sample report): they'd be broken once the form is gone.
    const reportIds = await this.ids(
      `SELECT id FROM report_definitions
       WHERE json_extract(data, '$.sampleScenario') = ?
          OR json_extract(data, '$.formId') IN (${placeholders(formIds)})`,
      [id, ...formIds],
    );

    if (checkpointIds.length === 0 && formIds.length === 0 && reportIds.length === 0) {
      return { success: false, reason: 'not_installed' };
    }

    this.log.info(`Removing sample scenario '${id}'`, { checkpointIds, formIds, reportIds });
    const removed: Record<string, number> = {};
    const del = async (table: string, rowIds: string[], pk = 'id') => {
      for (const rowId of rowIds) {
        await this.db.run(`DELETE FROM ${table} WHERE ${pk} = ?`, [rowId]);
      }
      removed[table] = (removed[table] ?? 0) + rowIds.length;
    };

    try {
      // Submissions, then photos no other submission references.
      const records = await this.db.exec(
        `SELECT id, data FROM interaction_records
         WHERE asset_id IN (${placeholders(checkpointIds)})
            OR interaction_type_id IN (${placeholders(formIds)})`,
        [...checkpointIds, ...formIds],
      );
      const blobHashes = new Set<string>();
      for (const record of records) {
        for (const match of String(record.data ?? '').matchAll(BLOB_REF)) {
          blobHashes.add(match[1]);
        }
      }
      await del(
        'interaction_records',
        records.map((r) => String(r.id)),
      );
      const orphanedHashes: string[] = [];
      for (const hash of blobHashes) {
        const stillUsed = await this.db.exec(
          `SELECT 1 FROM interaction_records WHERE instr(data, ?) > 0 LIMIT 1`,
          [`blob:sha256:${hash}`],
        );
        if (stillUsed.length === 0) orphanedHashes.push(hash);
      }
      await del('form_blobs', orphanedHashes, 'hash');

      // Labels / NFC tag mappings, numbered copies, form assignments.
      await del(
        'barcode_mappings',
        await this.ids(
          `SELECT id FROM barcode_mappings
           WHERE asset_id IN (${placeholders(checkpointIds)})
              OR interaction_type_id IN (${placeholders(formIds)})`,
          [...checkpointIds, ...formIds],
        ),
      );
      await del(
        'asset_instances',
        await this.ids(
          `SELECT id FROM asset_instances WHERE asset_id IN (${placeholders(checkpointIds)})`,
          checkpointIds,
        ),
      );
      await del(
        'asset_interaction_types',
        await this.ids(
          `SELECT id FROM asset_interaction_types
           WHERE asset_id IN (${placeholders(checkpointIds)})
              OR interaction_type_id IN (${placeholders(formIds)})`,
          [...checkpointIds, ...formIds],
        ),
      );

      // The tagged rows themselves.
      await del('assets', checkpointIds);
      await del('interaction_types', formIds);
      const definitionIds: string[] = [];
      for (const row of formRows) {
        const definitionId = row.definition_id ? String(row.definition_id) : '';
        if (!definitionId) continue;
        // Never delete a definition another (non-sample) form still points at.
        const sharedWith = await this.db.exec(
          `SELECT 1 FROM interaction_types WHERE json_extract(data, '$.form_definition_id') = ? LIMIT 1`,
          [definitionId],
        );
        if (sharedWith.length === 0) definitionIds.push(definitionId);
      }
      await del('form_definitions', definitionIds);
      await del('report_definitions', reportIds);
    } finally {
      this.notifyAll();
    }

    this.log.info(`Removed sample scenario '${id}'`, removed);
    return { success: true, removed };
  }

  private async countSubmissions(checkpointIds: string[], formIds: string[]): Promise<number> {
    if (checkpointIds.length === 0 && formIds.length === 0) return 0;
    const rows = await this.db.exec(
      `SELECT COUNT(*) AS count FROM interaction_records
       WHERE asset_id IN (${placeholders(checkpointIds)})
          OR interaction_type_id IN (${placeholders(formIds)})`,
      [...checkpointIds, ...formIds],
    );
    return Number(rows[0]?.count ?? 0);
  }

  private async ids(sql: string, params: unknown[]): Promise<string[]> {
    const rows = await this.db.exec(sql, params);
    return rows.map((r) => String(r.id));
  }

  private notifyAll(): void {
    for (const table of SAMPLE_TABLES) {
      this.db.notifyDataChange(table);
    }
  }
}

/**
 * `?, ?, ?` for an IN list. An empty list yields `NULL`, which matches nothing
 * (`x IN (NULL)` is never true), so callers needn't special-case it.
 */
function placeholders(values: unknown[]): string {
  return values.length === 0 ? 'NULL' : values.map(() => '?').join(', ');
}

/** Orders rows by where their `name` appears in `order`; unknown names go last. */
function sortByDefinitionOrder<T extends { name?: unknown }>(rows: T[], order: string[]): T[] {
  const rank = (row: T) => {
    const i = order.indexOf(String(row.name ?? ''));
    return i === -1 ? order.length : i;
  };
  return [...rows].sort((a, b) => rank(a) - rank(b));
}
